// src/Pages/conges/AdminConges.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, isValid, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { API_URL } from '../Config';
import {
  Search,
  Filter,
  Download,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  FileText,
  Calendar,
  User,
  Hash,
  AlertCircle
} from 'lucide-react';

const AdminConges = () => {
  const navigate = useNavigate();
  const [conges, setConges] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filtres et recherche
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('all');
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: '',
    dateType: 'creation'
  });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchAllConges();
  }, []);

  const fetchAllConges = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/conges`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          toast.warn('Accès refusé. Veuillez vous reconnecter.');
          navigate('/login');
          return;
        }
        throw new Error('Erreur lors du chargement des données');
      }

      const data = await response.json();
      setConges(data.data || data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des congés');
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  // Normalisation pour recherche
  const normalizeText = (text) => {
    return text
      ?.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') || '';
  };

  // Méthodes de recherche
  const searchById = (conge, term) => {
    return conge.id.toString().includes(term);
  };

  const searchByEmployee = (conge, term) => {
    const normalizedTerm = normalizeText(term);
    const employeeName = normalizeText(conge.user?.nom || '');
    const employeeEmail = normalizeText(conge.user?.email || '');
    
    return employeeName.includes(normalizedTerm) || 
           employeeEmail.includes(normalizedTerm);
  };

  const searchByMotif = (conge, term) => {
    const normalizedTerm = normalizeText(term);
    const motif = normalizeText(conge.motif || '');
    return motif.includes(normalizedTerm);
  };

  const searchAllFields = (conge, term) => {
    return (
      searchById(conge, term) ||
      searchByEmployee(conge, term) ||
      searchByMotif(conge, term)
    );
  };

  // Filtrage par date
  const filterByDate = (conge) => {
    if (!dateFilter.startDate && !dateFilter.endDate) return true;

    let dateToCheck;
    if (dateFilter.dateType === 'creation') {
      dateToCheck = parseISO(conge.created_at);
    } else if (dateFilter.dateType === 'periode') {
      dateToCheck = parseISO(conge.date_debut);
    }

    if (!isValid(dateToCheck)) return true;

    const startDate = dateFilter.startDate ? new Date(dateFilter.startDate) : null;
    const endDate = dateFilter.endDate ? new Date(dateFilter.endDate) : null;

    if (startDate && endDate) {
      return dateToCheck >= startDate && dateToCheck <= endDate;
    } else if (startDate) {
      return dateToCheck >= startDate;
    } else if (endDate) {
      return dateToCheck <= endDate;
    }

    return true;
  };

  // Filtrage principal
  const filteredConges = conges.filter((conge) => {
    // Filtre par statut
    let matchesFilter = true;
    if (filter !== 'all') {
      matchesFilter = {
        pending: conge.status === 'cree',
        validated: conge.status === 'valide_par_validateur',
        confirmed: conge.status === 'confirme_par_chef',
        refused: conge.status.includes('refuse'),
      }[filter] || true;
    }

    // Recherche textuelle
    let matchesSearch = true;
    if (searchTerm.trim()) {
      switch(searchType) {
        case 'id':
          matchesSearch = searchById(conge, searchTerm);
          break;
        case 'employee':
          matchesSearch = searchByEmployee(conge, searchTerm);
          break;
        case 'motif':
          matchesSearch = searchByMotif(conge, searchTerm);
          break;
        default:
          matchesSearch = searchAllFields(conge, searchTerm);
      }
    }

    // Filtre par date
    const matchesDate = filterByDate(conge);

    return matchesFilter && matchesSearch && matchesDate;
  });

  // Badges de statut
  const getStatusBadge = (status) => {
    const configs = {
      cree: { 
        label: 'En attente', 
        color: 'bg-amber-50 text-amber-700 border border-amber-200',
      },
      valide_par_validateur: { 
        label: 'Validé', 
        color: 'bg-blue-50 text-blue-700 border border-blue-200',
      },
      refuse_par_validateur: { 
        label: 'Refusé', 
        color: 'bg-red-50 text-red-700 border border-red-200',
      },
      confirme_par_chef: { 
        label: 'Confirmé', 
        color: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
      },
      refuse_par_chef: { 
        label: 'Refusé', 
        color: 'bg-red-50 text-red-700 border border-red-200',
      },
    };

    const config = configs[status] || { 
      label: 'Inconnu', 
      color: ' text-gray-700 border border-gray-200',
      icon: '❓'
    };

    return (
      <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${config.color}`}>
        <span>{config.icon}</span>
        <span>{config.label}</span>
      </div>
    );
  };

  const getTypeBadge = (type) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      type === 'exceptionnel'
        ? 'bg-purple-50 text-purple-700 border border-purple-200'
        : ' text-gray-700 border border-gray-200'
    }`}>
      {type === 'exceptionnel' ? 'Exceptionnel' : 'Normal'}
    </span>
  );

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy', { locale: fr });
    } catch (error) {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd/MM/yy HH:mm', { locale: fr });
    } catch (error) {
      return dateString;
    }
  };

  // Pagination
  const totalPages = Math.ceil(filteredConges.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedConges = filteredConges.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Export CSV
  const exportToCSV = () => {
    const headers = ['ID', 'Employé', 'Email', 'Type', 'Début', 'Fin', 'Durée', 'Statut', 'Motif', 'Date demande'];
    const rows = filteredConges.map(conge => [
      conge.id,
      conge.user?.nom || '',
      conge.user?.email || '',
      conge.type === 'exceptionnel' ? 'Exceptionnel' : 'Normal',
      formatDate(conge.date_debut),
      formatDate(conge.date_fin),
      conge.duree || '?',
      {
        cree: 'En attente',
        valide_par_validateur: 'Validé',
        refuse_par_validateur: 'Refusé',
        confirme_par_chef: 'Confirmé',
        refuse_par_chef: 'Refusé',
      }[conge.status] || conge.status,
      conge.motif || '',
      formatDateTime(conge.created_at),
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `conges_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    
    toast.success('Export CSV généré avec succès');
  };

  // Export XLSX
  const exportToXLSX = () => {
    const data = filteredConges.map(conge => ({
      'ID': conge.id,
      'Employé': conge.user?.nom || '',
      'Email': conge.user?.email || '',
      'Type': conge.type === 'exceptionnel' ? 'Exceptionnel' : 'Normal',
      'Date début': formatDate(conge.date_debut),
      'Date fin': formatDate(conge.date_fin),
      'Durée (jours)': conge.duree || 0,
      'Statut': {
        cree: 'En attente',
        valide_par_validateur: 'Validé',
        refuse_par_validateur: 'Refusé',
        confirme_par_chef: 'Confirmé',
        refuse_par_chef: 'Refusé',
      }[conge.status] || conge.status,
      'Motif': conge.motif || '',
      'Date création': formatDateTime(conge.created_at),
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Congés');
    
    // Auto-size columns
    const wscols = [
      { wch: 8 },  // ID
      { wch: 20 }, // Employé
      { wch: 25 }, // Email
      { wch: 12 }, // Type
      { wch: 12 }, // Date début
      { wch: 12 }, // Date fin
      { wch: 10 }, // Durée
      { wch: 12 }, // Statut
      { wch: 30 }, // Motif
      { wch: 16 }, // Date création
    ];
    worksheet['!cols'] = wscols;

    XLSX.writeFile(workbook, `conges_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Export Excel généré avec succès');
  };

  // Statistiques
  const stats = [
    { 
      label: 'Total', 
      value: conges.length, 
      color: ' border-gray-200 text-gray-700',
    },
    { 
      label: 'En attente', 
      value: conges.filter(c => c.status === 'cree').length, 
      color: 'bg-amber-50 border-amber-200 text-amber-700',
    },
    { 
      label: 'Validés', 
      value: conges.filter(c => c.status === 'valide_par_validateur').length, 
      color: 'bg-blue-50 border-blue-200 text-blue-700',
    },
    { 
      label: 'Confirmés', 
      value: conges.filter(c => c.status === 'confirme_par_chef').length, 
      color: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    },
    { 
      label: 'Refusés', 
      value: conges.filter(c => c.status.includes('refuse')).length, 
      color: 'bg-red-50 border-red-200 text-red-700',
    },
  ];

  const resetFilters = () => {
    setFilter('all');
    setSearchTerm('');
    setSearchType('all');
    setDateFilter({
      startDate: '',
      endDate: '',
      dateType: 'creation'
    });
    setCurrentPage(1);
    toast.info('Filtres réinitialisés');
  };

  const getSearchPlaceholder = () => {
    switch(searchType) {
      case 'id': return 'Rechercher par ID...';
      case 'employee': return 'Nom ou email employé...';
      case 'motif': return 'Motif de congé...';
      default: return 'Rechercher dans tous les champs...';
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
        <p className="text-gray-600">Chargement des congés...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-2">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Gestion des Congés</h1>
              <p className="text-gray-600 mt-1">Administration de toutes les demandes de congés</p>
            </div>
            <button
              onClick={fetchAllConges}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover: transition-colors"
            >
              <RefreshCw size={16} />
              <span className="text-sm font-medium">Actualiser</span>
            </button>
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {stats.map((stat, index) => (
            <div 
              key={index} 
              className={`p-4 rounded-xl border ${stat.color} transition-transform hover:scale-105`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium opacity-80">{stat.label}</p>
                  <p className="text-2xl font-bold mt-1">{stat.value}</p>
                </div>
                <span className="text-2xl">{stat.icon}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Panneau de contrôle */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-8 overflow-hidden">
          {/* En-tête du panneau */}
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Filter size={20} className="text-gray-400" />
                <h2 className="text-lg font-semibold text-gray-900">Filtres et Recherche</h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={exportToCSV}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:"
                >
                  <FileText size={16} />
                  CSV
                </button>
                <button
                  onClick={exportToXLSX}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700"
                >
                  <FileSpreadsheet size={16} />
                  Excel
                </button>
              </div>
            </div>
          </div>

          {/* Contenu du panneau */}
          <div className="p-6">
            

            {/* Recherche avancée */}
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Type de recherche */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <div className="flex items-center gap-2">
                      <Search size={16} />
                      Type de recherche
                    </div>
                  </label>
                  <select
                    value={searchType}
                    onChange={(e) => setSearchType(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">Tous les champs</option>
                    <option value="employee">
                      <div className="flex items-center gap-2">
                        <User size={14} />
                        Employé
                      </div>
                    </option>
                    <option value="id">
                      <div className="flex items-center gap-2">
                        <Hash size={14} />
                        ID
                      </div>
                    </option>
                    <option value="motif">
                      <div className="flex items-center gap-2">
                        <AlertCircle size={14} />
                        Motif
                      </div>
                    </option>
                  </select>
                </div>

                {/* Recherche */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Recherche
                  </label>
                  <div className="relative">
                    <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder={getSearchPlaceholder()}
                      value={searchTerm}
                      onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Type de date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <div className="flex items-center gap-2">
                      <Calendar size={16} />
                      Filtre par date
                    </div>
                  </label>
                  <select
                    value={dateFilter.dateType}
                    onChange={(e) => setDateFilter(prev => ({ ...prev, dateType: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="creation">Date de création</option>
                    <option value="periode">Période de congé</option>
                  </select>
                </div>

                {/* Réinitialisation */}
                <div className="flex items-end">
                  <button
                    onClick={resetFilters}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover: transition-colors"
                  >
                    Réinitialiser
                  </button>
                </div>
              </div>

              {/* Période */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Période :</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Date début</label>
                    <input
                      type="date"
                      value={dateFilter.startDate}
                      onChange={(e) => setDateFilter(prev => ({ ...prev, startDate: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Date fin</label>
                    <input
                      type="date"
                      value={dateFilter.endDate}
                      onChange={(e) => setDateFilter(prev => ({ ...prev, endDate: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex items-end">
                    <div className="text-sm text-gray-600 p-2.5  rounded-lg w-full">
                      {filteredConges.length} résultat{filteredConges.length > 1 ? 's' : ''} trouvé{filteredConges.length > 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Filtres rapides */}
            <div className="mb-6 pt-6 ">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => { setFilter('all'); setCurrentPage(1); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filter === 'all'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Tous
                </button>
                <button
                  onClick={() => { setFilter('pending'); setCurrentPage(1); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filter === 'pending'
                      ? 'bg-amber-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  En attente
                </button>
                <button
                  onClick={() => { setFilter('validated'); setCurrentPage(1); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filter === 'validated'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Validés
                </button>
                <button
                  onClick={() => { setFilter('confirmed'); setCurrentPage(1); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filter === 'confirmed'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Confirmés
                </button>
                <button
                  onClick={() => { setFilter('refused'); setCurrentPage(1); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filter === 'refused'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Refusés
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tableau */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Employé
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Période
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Durée
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Création
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {paginatedConges.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-16 text-center">
                      <div className="text-gray-400 mb-3">
                        <Search size={48} className="mx-auto opacity-50" />
                      </div>
                      <p className="text-gray-500 font-medium mb-2">Aucune demande trouvée</p>
                      <p className="text-gray-400 text-sm mb-4">
                        Essayez de modifier vos critères de recherche
                      </p>
                      <button
                        onClick={resetFilters}
                        className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                      >
                        Réinitialiser tous les filtres
                      </button>
                    </td>
                  </tr>
                ) : (
                  paginatedConges.map((conge) => (
                    <tr 
                      key={conge.id} 
                      className="hover: transition-colors cursor-pointer"
                      onClick={() => navigate(`/conges/${conge.id}`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">#{conge.id}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{conge.user?.nom || '-'}</div>
                        <div className="text-sm text-gray-500">{conge.user?.email || ''}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          <div className="font-medium">{formatDate(conge.date_debut)}</div>
                          <div className="text-gray-500">au {formatDate(conge.date_fin)}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {conge.duree} jour{conge.duree > 1 ? 's' : ''}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getTypeBadge(conge.type)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(conge.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {formatDateTime(conge.created_at)}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 ">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm text-gray-500">
                  Affichage de <span className="font-medium">{startIndex + 1}</span> à{' '}
                  <span className="font-medium">{Math.min(startIndex + itemsPerPage, filteredConges.length)}</span>{' '}
                  sur <span className="font-medium">{filteredConges.length}</span> résultats
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover: disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`min-w-[40px] px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover: disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminConges;